import { Button, Input } from '@affine/component';
import {
  SettingsChosenEntry,
  SettingsField,
  SettingsHint,
  SettingsNote,
} from '@affine/component/setting-components';
import { SettingsSelect } from '@affine/core/desktop/dialogs/setting/shared/ui/settings-select';
import { useWorkspaceCredentials } from '@affine/core/desktop/dialogs/setting/workspace-setting/credentials/use-workspace-credentials';
import { SettingsEntityFormDialog } from '@affine/core/desktop/dialogs/setting/workspace-setting/shared/settings-form-dialog';
import {
  SettingsServiceGrid,
  SettingsServiceLogo,
} from '@affine/core/desktop/dialogs/setting/workspace-setting/shared/settings-service-picker';
import { defaultWorkspaceApiClient } from '@affine/core/modules/cloud';
import { useI18n } from '@affine/i18n';
import { ArrowLeftSmallIcon, InformationIcon } from '@blocksuite/icons/rc';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { connectionApi } from './connection-api';
import { describeConnectionFailure } from './connection-failure';
import {
  ConnectionSetupFields,
  EMPTY_SETUP_VALUES,
  isSetupComplete,
  toSetupPayload,
} from './connection-setup-fields';
import type {
  ConnectionServiceDefinition,
  WorkspaceConnectionSummary,
} from './types';
import { useOnline } from './use-online';

/** The credential choice that creates new credentials from the setup fields. */
const CREATE = 'create';
const GRID_WIDTH = 640;
/** Roles that may hand a credential to a connection. */
const USING_ROLES = ['Owner', 'Admin', 'Editor'];

export interface ConnectServiceDialogProps {
  readonly workspaceId: string;
  /** The services a connection can be made to; one of them opens its form directly. */
  readonly services: readonly ConnectionServiceDefinition[];
  readonly servicesLoading?: boolean;
  readonly readOnly?: boolean;
  readonly onConnected: (connection: WorkspaceConnectionSummary) => void;
  readonly onClose: () => void;
}

/** Secrets typed for one workspace never reach the dialog of another. */
export function ConnectServiceDialog(props: ConnectServiceDialogProps) {
  return <ConnectServiceDialogForm key={props.workspaceId} {...props} />;
}

/**
 * Connects a service in two steps, as adding a credential does: the service
 * is picked from its logo grid, then the connection is named and given
 * credentials, existing ones or new ones from the service's setup fields.
 */
function ConnectServiceDialogForm({
  workspaceId,
  services,
  servicesLoading = false,
  readOnly = false,
  onConnected,
  onClose,
}: ConnectServiceDialogProps) {
  const t = useI18n();
  const online = useOnline();
  const hasGrid = servicesLoading || services.length > 1;
  const [service, setService] = useState<ConnectionServiceDefinition | null>(
    null
  );
  const [label, setLabel] = useState('');
  const [credentialLabel, setCredentialLabel] = useState('');
  const [chosenCredential, setChosenCredential] = useState<string | null>(null);
  const [setupValues, setSetupValues] = useState(EMPTY_SETUP_VALUES);
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const { credentials } = useWorkspaceCredentials();

  const pick = useCallback((next: ConnectionServiceDefinition) => {
    setService(next);
    setLabel(next.name);
    setCredentialLabel(next.name);
    setChosenCredential(null);
    setSetupValues(EMPTY_SETUP_VALUES);
    setError(undefined);
  }, []);

  const onlyService = hasGrid ? null : (services[0] ?? null);
  useEffect(() => {
    if (service === null && onlyService) pick(onlyService);
  }, [onlyService, pick, service]);

  const compatibleCredentials = useMemo(
    () =>
      service
        ? credentials.filter(
            credential =>
              credential.service === service.slug &&
              USING_ROLES.includes(credential.effectiveRole ?? '') &&
              service.connectionDefinition.credentialTypes.includes(
                credential.type
              )
          )
        : [],
    [credentials, service]
  );
  const setupFields = service?.connectionDefinition.setupFields ?? [];
  // A service without setup fields can only reuse credentials: the first
  // fitting one is chosen until the user picks another.
  const credentialChoice =
    chosenCredential ??
    (setupFields.length === 0 && compatibleCredentials.length > 0
      ? compatibleCredentials[0].id
      : CREATE);
  const creates = credentialChoice === CREATE;
  const locked = submitting || readOnly || !online;

  const valid =
    service !== null &&
    label.trim().length > 0 &&
    (creates
      ? credentialLabel.trim().length > 0 &&
        isSetupComplete(setupFields, setupValues)
      : true);

  const cancel = () => {
    if (submittingRef.current) return;
    setSetupValues(EMPTY_SETUP_VALUES);
    onClose();
  };

  const submit = async () => {
    if (!service || submittingRef.current || !valid || readOnly || !online) {
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setError(undefined);
    try {
      const connection = await connectionApi.create(workspaceId, {
        serviceDefinitionId: service.id,
        label: label.trim(),
        credential: creates
          ? {
              kind: 'create',
              label: credentialLabel.trim(),
              ...toSetupPayload(setupValues),
            }
          : { kind: 'existing', credentialId: credentialChoice },
      });
      setSetupValues(EMPTY_SETUP_VALUES);
      onConnected(connection);
    } catch (cause) {
      setError(describeConnectionFailure(t, cause));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <SettingsEntityFormDialog
      open
      onOpenChange={open => {
        if (!open) cancel();
      }}
      title={
        service
          ? t['com.streamlain.connections.connect-title']({
              service: service.name,
            })
          : t['com.streamlain.connections.pick-service-title']()
      }
      width={service ? undefined : GRID_WIDTH}
      onSave={() => void submit()}
      onCancel={cancel}
      saveLabel={t['com.streamlain.connections.connect']()}
      cancelLabel={t['Cancel']()}
      saveDisabled={!valid || readOnly || !online}
      saving={submitting}
      savingLabel={t['com.streamlain.connections.connecting']()}
      contentTestId="connection-connect-dialog"
      saveTestId="connection-connect-save"
    >
      {service === null ? (
        <SettingsServiceGrid
          choices={services}
          isLoading={servicesLoading}
          testIdPrefix="connection-service"
          onPick={pick}
        />
      ) : (
        <>
          <SettingsChosenEntry
            logo={<SettingsServiceLogo service={service} />}
            name={service.name}
            action={
              hasGrid ? (
                <Button
                  variant="plain"
                  prefix={<ArrowLeftSmallIcon />}
                  disabled={submitting}
                  onClick={() => {
                    setSetupValues(EMPTY_SETUP_VALUES);
                    setService(null);
                  }}
                  data-testid="connection-service-back"
                >
                  {t[
                    'com.streamlain.settings.workspace.credentials.form.other-service'
                  ]()}
                </Button>
              ) : null
            }
          />
          {!online ? (
            <SettingsNote
              icon={<InformationIcon />}
              tone="warning"
              role="status"
            >
              {t['com.streamlain.connections.offline']()}
            </SettingsNote>
          ) : null}
          {readOnly ? (
            <SettingsNote icon={<InformationIcon />} role="status">
              {t['com.streamlain.connections.no-manage-permission']()}
            </SettingsNote>
          ) : null}
          <SettingsField
            label={t['com.streamlain.connections.connection-name']()}
          >
            {({ labelId }) => (
              <Input
                aria-labelledby={labelId}
                data-testid="connection-name"
                autoFocus
                value={label}
                onChange={setLabel}
                disabled={locked}
              />
            )}
          </SettingsField>
          {compatibleCredentials.length > 0 ? (
            <SettingsField label={t['com.streamlain.connections.credential']()}>
              {({ labelId }) => (
                <SettingsSelect
                  labelledBy={labelId}
                  testId="connection-credential"
                  value={credentialChoice}
                  disabled={locked}
                  onChange={setChosenCredential}
                  options={[
                    {
                      value: CREATE,
                      label: t['com.streamlain.connections.create-new'](),
                    },
                    ...compatibleCredentials.map(credential => ({
                      value: credential.id,
                      label: credential.name,
                    })),
                  ]}
                />
              )}
            </SettingsField>
          ) : null}
          {creates ? (
            <>
              <SettingsField
                label={t['com.streamlain.connections.credential-name']()}
              >
                {({ labelId }) => (
                  <Input
                    aria-labelledby={labelId}
                    data-testid="connection-credential-name"
                    value={credentialLabel}
                    onChange={setCredentialLabel}
                    disabled={locked}
                  />
                )}
              </SettingsField>
              <ConnectionSetupFields
                setupFields={setupFields}
                values={setupValues}
                disabled={locked}
                onChange={setSetupValues}
              />
            </>
          ) : null}
          {error ? (
            <SettingsHint tone="error" role="alert">
              {error}
            </SettingsHint>
          ) : null}
        </>
      )}
    </SettingsEntityFormDialog>
  );
}

export function useConnectionServices(workspaceId: string) {
  const t = useI18n();
  const [services, setServices] = useState<
    readonly ConnectionServiceDefinition[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const generation = useRef(0);
  const reload = useCallback(async () => {
    const current = ++generation.current;
    setServices([]);
    setLoading(true);
    setError(undefined);
    try {
      const response = await defaultWorkspaceApiClient.request({
        workspaceId,
        path: '/api/service-definitions',
        category: 'settings',
        key: 'connections:service-definitions',
        mode: 'background',
        priority: 'visible',
      });
      if (!response.ok) {
        throw new Error(t['com.streamlain.connections.catalog-failed']());
      }
      const rows = (await response.json()) as ConnectionServiceDefinition[];
      if (generation.current === current) {
        setServices(
          rows.filter(row => row.connectionDefinition.mode !== 'none')
        );
      }
    } catch (cause) {
      if (generation.current !== current) return;
      setError(
        cause instanceof Error
          ? cause.message
          : t['com.streamlain.connections.catalog-failed']()
      );
    } finally {
      if (generation.current === current) setLoading(false);
    }
  }, [t, workspaceId]);
  useEffect(() => {
    void reload();
    return () => {
      generation.current += 1;
    };
  }, [reload]);
  return { services, loading, error, reload };
}
